import { useState, useEffect, useCallback } from 'react';
import { profileAPI, dataRoleAPI, permissionAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Loader2, Shield, Lock, Copy, Trash2, Eye, Globe, User, Users } from 'lucide-react';

const TAB_PROFILES = 'profiles';
const TAB_DATA_ROLES = 'data_roles';

export default function RolesPage() {
  const [activeTab, setActiveTab] = useState(TAB_PROFILES);
  const [profiles, setProfiles] = useState([]);
  const [dataRoles, setDataRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { canAccess } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, dataRoleRes, modulesRes] = await Promise.all([
        profileAPI.list(),
        dataRoleAPI.list(),
        permissionAPI.getModules(),
      ]);
      setProfiles(profileRes.data.profiles || []);
      setDataRoles(dataRoleRes.data.data_roles || []);
      setModules(modulesRes.data.modules || []);
      setActions(modulesRes.data.actions || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const canEdit = canAccess('ROLES_PROFILES', 'EDIT');
  const canCreate = canAccess('ROLES_PROFILES', 'CREATE');
  const canDelete = canAccess('ROLES_PROFILES', 'DELETE');

  return (
    <div className="space-y-6" data-testid="roles-profiles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Roles & Profiles</h1>
          <p className="text-muted-foreground text-sm">Manage access control profiles and data visibility roles</p>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setActiveTab(TAB_PROFILES)}
          data-testid="tab-profiles"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === TAB_PROFILES
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Shield className="h-4 w-4 inline mr-2" />
          Profiles
        </button>
        <button
          onClick={() => setActiveTab(TAB_DATA_ROLES)}
          data-testid="tab-data-roles"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === TAB_DATA_ROLES
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Eye className="h-4 w-4 inline mr-2" />
          Data Roles
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : activeTab === TAB_PROFILES ? (
        <ProfilesTab
          profiles={profiles}
          modules={modules}
          actions={actions}
          canEdit={canEdit}
          canCreate={canCreate}
          canDelete={canDelete}
          onRefresh={fetchData}
        />
      ) : (
        <DataRolesTab
          dataRoles={dataRoles}
          canEdit={canEdit}
          canCreate={canCreate}
          onRefresh={fetchData}
        />
      )}
    </div>
  );
}

/* ===================== PROFILES TAB ===================== */
function ProfilesTab({ profiles, modules, actions, canEdit, canCreate, canDelete, onRefresh }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', module_permissions: {} });

  const coreModules = modules.filter(m => m.group === 'core');
  const toolModules = modules.filter(m => m.group === 'tools');
  const adminModules = modules.filter(m => m.group === 'admin');

  const openModal = (profile = null) => {
    if (profile) {
      setEditingProfile(profile);
      setFormData({
        name: profile.name,
        description: profile.description || '',
        module_permissions: { ...(profile.module_permissions || {}) },
      });
    } else {
      setEditingProfile(null);
      setFormData({ name: '', description: '', module_permissions: {} });
    }
    setModalOpen(true);
  };

  const toggleAction = (moduleId, action) => {
    setFormData(prev => {
      const mp = { ...prev.module_permissions };
      const current = mp[moduleId] || [];
      if (current.includes(action)) {
        mp[moduleId] = current.filter(a => a !== action);
        if (mp[moduleId].length === 0) delete mp[moduleId];
      } else {
        mp[moduleId] = [...current, action];
      }
      return { ...prev, module_permissions: mp };
    });
  };

  const toggleAllModule = (moduleId, checked) => {
    setFormData(prev => {
      const mp = { ...prev.module_permissions };
      if (checked) {
        mp[moduleId] = [...actions];
      } else {
        delete mp[moduleId];
      }
      return { ...prev, module_permissions: mp };
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error('Profile name is required'); return; }
    setSubmitting(true);
    try {
      if (editingProfile) {
        await profileAPI.update(editingProfile.id, formData);
        toast.success('Profile updated');
      } else {
        await profileAPI.create(formData);
        toast.success('Profile created');
      }
      setModalOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClone = async (profile) => {
    try {
      await profileAPI.clone(profile.id, { name: `${profile.name} (Copy)` });
      toast.success('Profile cloned');
      onRefresh();
    } catch (error) {
      toast.error('Failed to clone profile');
    }
  };

  const handleDelete = async (profileId) => {
    try {
      await profileAPI.delete(profileId);
      toast.success('Profile deleted');
      setDeleteConfirm(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete profile');
    }
  };

  const getPermCount = (profile) => {
    const mp = profile.module_permissions || {};
    return Object.values(mp).reduce((sum, actions) => sum + actions.length, 0);
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Profiles control <strong>what actions</strong> users can perform on each module.
        </p>
        {canCreate && (
          <Button onClick={() => openModal()} className="rounded-sm" data-testid="create-profile-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Profile
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {profiles.map(profile => (
          <Card key={profile.id} className="rounded-sm" data-testid={`profile-card-${profile.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  {profile.name}
                </CardTitle>
                <div className="flex gap-1">
                  {canCreate && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleClone(profile)}
                      data-testid={`clone-profile-${profile.id}`} title="Clone">
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canEdit && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openModal(profile)}
                      data-testid={`edit-profile-${profile.id}`} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {canDelete && !profile.is_system && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDeleteConfirm(profile)}
                      data-testid={`delete-profile-${profile.id}`} title="Delete">
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
              {profile.is_system && (
                <Badge variant="outline" className="w-fit text-xs flex items-center gap-1 mt-1">
                  <Lock className="h-3 w-3" />System
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">{profile.description || 'No description'}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Permissions</span>
                <Badge variant="secondary" className="text-xs rounded-sm font-mono">
                  {getPermCount(profile)} actions
                </Badge>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {Object.keys(profile.module_permissions || {}).slice(0, 4).map(mod => (
                  <Badge key={mod} variant="outline" className="text-xs rounded-sm">{mod}</Badge>
                ))}
                {Object.keys(profile.module_permissions || {}).length > 4 && (
                  <Badge variant="outline" className="text-xs rounded-sm">
                    +{Object.keys(profile.module_permissions || {}).length - 4} more
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Profile Edit/Create Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {editingProfile ? `Edit Profile: ${editingProfile.name}` : 'Create New Profile'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Profile Name *</Label>
                <Input
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Senior Buyer"
                  className="rounded-sm mt-1"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Description</Label>
                <Input
                  value={formData.description}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Brief description..."
                  className="rounded-sm mt-1"
                />
              </div>
            </div>

            {/* Permission Matrix */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Module Permissions</Label>
              <ScrollArea className="h-[400px] border rounded-sm">
                <div className="min-w-[600px]">
                  {/* Matrix Header */}
                  <div className="grid sticky top-0 z-10 bg-muted/95 backdrop-blur border-b"
                    style={{ gridTemplateColumns: '200px repeat(7, 1fr)' }}>
                    <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase">Module</div>
                    {actions.map(action => (
                      <div key={action} className="px-2 py-2 text-xs font-medium text-muted-foreground text-center uppercase">
                        {action}
                      </div>
                    ))}
                  </div>

                  {/* Core Modules */}
                  <ModuleGroup label="Core Modules" modules={coreModules} actions={actions}
                    permissions={formData.module_permissions} onToggle={toggleAction} onToggleAll={toggleAllModule} />

                  {/* Tools */}
                  <ModuleGroup label="Tools" modules={toolModules} actions={actions}
                    permissions={formData.module_permissions} onToggle={toggleAction} onToggleAll={toggleAllModule} />

                  {/* Admin */}
                  <ModuleGroup label="Admin" modules={adminModules} actions={actions}
                    permissions={formData.module_permissions} onToggle={toggleAction} onToggleAll={toggleAllModule} />
                </div>
              </ScrollArea>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setModalOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="rounded-sm" data-testid="save-profile-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingProfile ? 'Update Profile' : 'Create Profile')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Profile</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? Users currently assigned this profile will lose their permissions.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-sm">Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} className="rounded-sm"
              data-testid="confirm-delete-profile">
              Delete Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ===================== MODULE GROUP ===================== */
function ModuleGroup({ label, modules, actions, permissions, onToggle, onToggleAll }) {
  if (modules.length === 0) return null;
  return (
    <>
      <div className="px-3 py-1.5 bg-muted/30 border-b">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      {modules.map(mod => {
        const modPerms = permissions[mod.id] || [];
        const allChecked = actions.every(a => modPerms.includes(a));
        return (
          <div key={mod.id}
            className="grid border-b last:border-b-0 hover:bg-muted/20 transition-colors"
            style={{ gridTemplateColumns: '200px repeat(7, 1fr)' }}
            data-testid={`module-row-${mod.id}`}
          >
            <div className="px-3 py-2 flex items-center gap-2">
              <Checkbox
                checked={allChecked}
                onCheckedChange={(checked) => onToggleAll(mod.id, !!checked)}
                data-testid={`module-all-${mod.id}`}
              />
              <span className="text-sm font-medium">{mod.name}</span>
            </div>
            {actions.map(action => (
              <div key={action} className="flex items-center justify-center py-2">
                <Checkbox
                  checked={modPerms.includes(action)}
                  onCheckedChange={() => onToggle(mod.id, action)}
                  data-testid={`perm-${mod.id}-${action}`}
                />
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}

/* ===================== DATA ROLES TAB ===================== */
function DataRolesTab({ dataRoles, canEdit, canCreate, onRefresh }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', scope: 'OWN_ONLY' });

  const scopeConfig = {
    ALL: { label: 'All Data', icon: Globe, color: 'bg-blue-100 text-blue-800', desc: 'Can see all records across the organization' },
    ASSIGNED: { label: 'Assigned Only', icon: Users, color: 'bg-amber-100 text-amber-800', desc: 'Can only see data for assigned items/categories' },
    OWN_ONLY: { label: 'Own Records', icon: User, color: 'bg-slate-100 text-slate-700', desc: 'Can only see records they created' },
    TEAM: { label: 'Team', icon: Users, color: 'bg-green-100 text-green-800', desc: 'Can see records from users in the same team' },
  };

  const openModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({ name: role.name, description: role.description || '', scope: role.scope || 'OWN_ONLY' });
    } else {
      setEditingRole(null);
      setFormData({ name: '', description: '', scope: 'OWN_ONLY' });
    }
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      if (editingRole) {
        await dataRoleAPI.update(editingRole.id, formData);
        toast.success('Data role updated');
      } else {
        await dataRoleAPI.create(formData);
        toast.success('Data role created');
      }
      setModalOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save data role');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Data Roles control <strong>what data</strong> users can see (visibility scope).
        </p>
        {canCreate && (
          <Button onClick={() => openModal()} className="rounded-sm" data-testid="create-data-role-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Data Role
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {dataRoles.map(role => {
          const cfg = scopeConfig[role.scope] || scopeConfig.OWN_ONLY;
          const Icon = cfg.icon;
          return (
            <Card key={role.id} className="rounded-sm" data-testid={`data-role-card-${role.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className="h-4 w-4 text-primary" />
                    {role.name}
                  </CardTitle>
                  {canEdit && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openModal(role)}
                      data-testid={`edit-data-role-${role.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                {role.is_system && (
                  <Badge variant="outline" className="w-fit text-xs flex items-center gap-1 mt-1">
                    <Lock className="h-3 w-3" />System
                  </Badge>
                )}
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-2">{role.description || 'No description'}</p>
                <Badge className={`text-xs rounded-sm ${cfg.color}`}>{cfg.label}</Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Data Role Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Data Role' : 'Create Data Role'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Name *</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Regional Manager"
                className="rounded-sm mt-1"
                data-testid="data-role-name-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Description</Label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description..."
                className="rounded-sm mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Data Visibility Scope</Label>
              <Select value={formData.scope} onValueChange={v => setFormData(p => ({ ...p, scope: v }))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="data-role-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(scopeConfig).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <cfg.icon className="h-3.5 w-3.5" />
                        {cfg.label} - {cfg.desc}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setModalOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="rounded-sm" data-testid="save-data-role-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingRole ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
